import React, { useState, useEffect } from "react";
import { base44 } from "@voult/api/base44Client";
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, PieChart, Pie, Cell } from "recharts";
import { Loader2, TrendingUp, CreditCard, Key, Activity } from "lucide-react";

const BRAND_COLORS_PIE = { visa: "#3b82f6", mastercard: "#f97316", amex: "#10b981", discover: "#f59e0b", unknown: "#94a3b8" };

export default function UsageAnalytics() {
  const [cards, setCards] = useState([]);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      base44.entities.Card.list("-created_date", 200),
      base44.entities.ActivityLog.list("-created_date", 200),
    ]).then(([c, l]) => { setCards(c || []); setLogs(l || []); }).finally(() => setLoading(false));
  }, []);

  // Cards added per day (last 7 days)
  const cardsByDay = (() => {
    const days = Array.from({ length: 7 }, (_, i) => {
      const d = new Date(); d.setDate(d.getDate() - (6 - i));
      return { label: d.toLocaleDateString("en", { weekday: "short" }), date: d.toDateString(), count: 0 };
    });
    cards.forEach((c) => {
      const ds = c.created_date ? new Date(c.created_date).toDateString() : null;
      const d = days.find((x) => x.date === ds);
      if (d) d.count++;
    });
    return days;
  })();

  // Activity by action type
  const actionCounts = logs.reduce((acc, l) => { acc[l.action] = (acc[l.action] || 0) + 1; return acc; }, {});
  const actionData = Object.entries(actionCounts).map(([name, value]) => ({ name: name.replace(/_/g, " "), value }));

  // Brand distribution
  const brandCounts = cards.reduce((acc, c) => { acc[c.brand] = (acc[c.brand] || 0) + 1; return acc; }, {});
  const brandData = Object.entries(brandCounts).map(([name, value]) => ({ name, value }));

  const stats = [
    { label: "Total Cards", value: cards.length, icon: CreditCard, color: "bg-indigo-50 text-indigo-600" },
    { label: "Total Events", value: logs.length, icon: Activity, color: "bg-emerald-50 text-emerald-600" },
    { label: "Brands", value: Object.keys(brandCounts).length, icon: TrendingUp, color: "bg-orange-50 text-orange-600" },
    { label: "This Week", value: cardsByDay.reduce((s, d) => s + d.count, 0), icon: Key, color: "bg-purple-50 text-purple-600" },
  ];

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <h1 className="text-2xl font-semibold text-slate-900 mb-1">Usage Analytics</h1>
      <p className="text-sm text-slate-500 mb-6">Card and token activity trends.</p>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {stats.map(({ label, value, icon: Icon, color }) => (
          <div key={label} className="bg-white rounded-2xl border border-slate-200 p-4 flex items-center gap-3">
            <div className={`h-10 w-10 rounded-xl flex items-center justify-center flex-shrink-0 ${color}`}><Icon className="h-5 w-5" /></div>
            <div><p className="text-2xl font-semibold text-slate-900">{value}</p><p className="text-xs text-slate-500">{label}</p></div>
          </div>
        ))}
      </div>

      <div className="grid lg:grid-cols-2 gap-5">
        <div className="bg-white rounded-2xl border border-slate-200 p-5">
          <h2 className="text-sm font-semibold text-slate-900 mb-4">Cards Added (Last 7 Days)</h2>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={cardsByDay}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#94a3b8" }} />
              <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#94a3b8" }} />
              <Tooltip />
              <Bar dataKey="count" fill="#1e293b" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 p-5">
          <h2 className="text-sm font-semibold text-slate-900 mb-4">Activity by Type</h2>
          {actionData.length === 0 ? (
            <p className="text-sm text-slate-400 py-10 text-center">No activity yet.</p>
          ) : (
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={actionData} layout="vertical">
                <XAxis type="number" tick={{ fontSize: 11, fill: "#94a3b8" }} allowDecimals={false} />
                <YAxis dataKey="name" type="category" tick={{ fontSize: 10, fill: "#94a3b8" }} width={100} />
                <Tooltip />
                <Bar dataKey="value" fill="#6366f1" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 p-5 lg:col-span-2">
          <h2 className="text-sm font-semibold text-slate-900 mb-4">Card Brand Distribution</h2>
          {brandData.length === 0 ? (
            <p className="text-sm text-slate-400 py-10 text-center">No cards yet.</p>
          ) : (
            <div className="flex items-center gap-8">
              <ResponsiveContainer width={200} height={200}>
                <PieChart>
                  <Pie data={brandData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80}>
                    {brandData.map((entry) => <Cell key={entry.name} fill={BRAND_COLORS_PIE[entry.name] || "#94a3b8"} />)}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
              <div className="space-y-2">
                {brandData.map((b) => (
                  <div key={b.name} className="flex items-center gap-2 text-sm">
                    <div className="h-3 w-3 rounded-full" style={{ background: BRAND_COLORS_PIE[b.name] || "#94a3b8" }} />
                    <span className="capitalize text-slate-700">{b.name}</span>
                    <span className="text-slate-400">({b.value})</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}